export type InAppBrowser =
  | 'kakaotalk'
  | 'instagram'
  | 'facebook'
  | 'line'
  | 'naver'
  | 'whale'
  | 'twitter'
  | 'telegram'
  | 'wechat'
  | 'whatsapp';

export interface Platform {
  iOS: boolean;
  android: boolean;
  desktop: boolean;
  isMobile: boolean;
  inAppBrowser: InAppBrowser | null;
  isWebView: boolean;
  ua: string;
}

const IN_APP_PATTERNS: { name: InAppBrowser; pattern: RegExp }[] = [
  { name: 'kakaotalk', pattern: /kakaotalk/ },
  { name: 'instagram', pattern: /instagram/ },
  { name: 'facebook', pattern: /fb_iab|fbav|facebookapp/ },
  // line 은 약자라 단어 경계 필요. android 라인 앱 UA 예: " Line/12.x.x"
  { name: 'line', pattern: / line\//i },
  { name: 'naver', pattern: /naver\(|naverapp|naver mobile/ },
  { name: 'whale', pattern: /whale/ },
  { name: 'twitter', pattern: /twitter/ },
  { name: 'telegram', pattern: /telegram/ },
  { name: 'wechat', pattern: /micromessenger|wechat/ },
  { name: 'whatsapp', pattern: /whatsapp/ },
];

function getResolvedUa(uaOverride?: string | null): string {
  if (uaOverride && uaOverride.length > 0) return uaOverride.toLowerCase();
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent.toLowerCase();
}

export function detectPlatform(uaOverride?: string | null): Platform {
  const ua = getResolvedUa(uaOverride);

  if (!ua) {
    return {
      iOS: false,
      android: false,
      desktop: true,
      isMobile: false,
      inAppBrowser: null,
      isWebView: false,
      ua: '',
    };
  }

  const iOS = /iphone|ipad|ipod/.test(ua);
  const android = /android/.test(ua);
  const isMobile = iOS || android;

  let inAppBrowser: InAppBrowser | null = null;
  for (const { name, pattern } of IN_APP_PATTERNS) {
    if (pattern.test(ua)) {
      inAppBrowser = name;
      break;
    }
  }

  // iOS Safari 정의: 'safari' + 'version' 모두 포함, 그리고 다른 브라우저 마커가 없음
  const isIOSSafari =
    iOS &&
    ua.includes('safari') &&
    ua.includes('version') &&
    !ua.includes('crios') &&
    !ua.includes('fxios') &&
    !ua.includes('edgios');

  // WebView: iOS 의 비-Safari 환경 (인앱브라우저 제외) 또는 Android 의 'wv'
  const isWebView =
    (iOS && !isIOSSafari && !inAppBrowser) || /\swv[);]/.test(ua);

  return {
    iOS,
    android,
    desktop: !isMobile,
    isMobile,
    inAppBrowser,
    isWebView,
    ua,
  };
}

/** 사람이 읽기 쉬운 인앱브라우저 한국어 이름 */
export function inAppBrowserDisplayName(ib: InAppBrowser): string {
  switch (ib) {
    case 'kakaotalk':
      return '카카오톡';
    case 'instagram':
      return '인스타그램';
    case 'facebook':
      return '페이스북';
    case 'line':
      return '라인';
    case 'naver':
      return '네이버';
    case 'whale':
      return '웨일';
    case 'twitter':
      return '트위터';
    case 'telegram':
      return '텔레그램';
    case 'wechat':
      return '위챗';
    case 'whatsapp':
      return '왓츠앱';
  }
}
